import React from "react";
import { Link } from "react-router-dom";

import {
  Card,
  CardContent,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { SelectField } from "react-admin";

import { UserStatuses } from "../../../../utils/constants";
import { hasIdNameMismatch } from "../../../../utils/carrierUserUtils";
import { UserStatusIcon } from "../../users/UserStatusIcon";
import { ImageField } from "../../../../components/common/ImageField";

const ID_DOC_TYPE_LABELS = {
  drivers_license: "Driver's License",
  state_id: "State ID",
  other: "Other ID",
};

/**
 * Parsed ID-document facts for a gate-enrolled driver. The identity arrives
 * via useDriverIdentity in the edit screen (manager-gated driverAdmin
 * endpoint — the table is admin-locked in AppSync). Renders nothing until the
 * lookup answers; a driver with no identity row (404) or a transient failure
 * just leaves the aside without the panel.
 */
// Expiry rows hold either ISO (newer) or the raw printed date (older). A
// date-only ISO string fed to Date() lands at UTC midnight, which
// toLocaleDateString shows as the previous day west of Greenwich — so build
// those as a local date instead.
const parseExpiry = (raw) => {
  if (!raw) return null;
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const date = isoDay
    ? new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]))
    : new Date(raw);
  return isNaN(date) ? null : date;
};

const DriverIdentityPanel = ({ identity, nameMismatch }) => {
  if (!identity) return null;

  const expiry = parseExpiry(identity.idDocExpiry);

  const rows = [
    ["Type", ID_DOC_TYPE_LABELS[identity.idDocType] || identity.idDocType],
    ["Last name", identity.idDocLastName],
    // The first name is never stored past its initial, which is also all the
    // mismatch check compares — showing it lets a manager see what the check saw.
    ["First initial", identity.idDocFirstInitial],
    ["Number ends in", identity.idDocLast4],
    ["State", identity.idDocState],
    ["Class", identity.idDocClass],
    // Unparseable legacy value still shows as printed on the license.
    ["Expires", expiry ? expiry.toLocaleDateString() : identity.idDocExpiry],
    [
      "Captured",
      identity.idDocCapturedAt &&
        new Date(identity.idDocCapturedAt).toLocaleDateString(),
    ],
    // Typed off the card by staff rather than read from a photo: weaker proof,
    // and there is no OCR'd name for the mismatch check to compare.
    ["Entered", identity.idDocSource === "manual" && "Typed by hand"],
  ].filter(([, value]) => value);
  // Created from the Drivers tab (driverAdmin createDriver), not signed up by
  // the driver in the Driver App.
  const byStaff = identity.enrolledVia === "manager";

  if (!rows.length && !byStaff && identity.status !== "PURGED") return null;

  // Valid THROUGH the printed date, so the badge only turns red once that day
  // is over — parseExpiry lands on local midnight, which would otherwise read
  // "expired" for the whole of a license's last valid day.
  const expiryMs = expiry ? expiry.getTime() + 86400000 - 1 : null;
  const expired = expiryMs !== null && expiryMs < Date.now();
  const expiringSoon =
    expiryMs !== null &&
    !expired &&
    expiryMs < Date.now() + 30 * 24 * 60 * 60 * 1000;

  return (
    <Grid item xs={12}>
      <Typography variant="overline" color="textSecondary">
        ID Document:
      </Typography>
      {identity.status === "PURGED" ? (
        <Typography variant="body2">Deleted (consent withdrawn)</Typography>
      ) : (
        <>
          {rows.map(([label, value]) => (
            <Typography key={label} variant="body2">
              {label}: {value}
            </Typography>
          ))}
          {expired && (
            <Typography variant="body2" color="error">
              License expired
            </Typography>
          )}
          {expiringSoon && (
            <Typography variant="body2" color="error">
              License expires within 30 days
            </Typography>
          )}
          {nameMismatch && (
            <Typography variant="body2" color="error">
              Name on document differs from enrolled name
            </Typography>
          )}
          {!rows.length && (
            <Typography variant="body2">None on file</Typography>
          )}
          {byStaff && (
            <Typography variant="body2" color="textSecondary">
              Created by yard staff
            </Typography>
          )}
        </>
      )}
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "16em",
      minWidth: "16em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  statusIcon: {
    marginTop: theme.spacing(-0.3),
  },
}));

export const CarrierUserAside = ({ backPath, record, identity, ...props }) => {
  const classes = useStyles();
  const carrier = record?.reloadYardCarriers?.items[0];
  const nameMismatch = hasIdNameMismatch(record, identity);
  const linkBackPath = backPath
    ? [...backPath, props.location.pathname]
    : [props.location.pathname];

  if (!record) return null;
  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary">
              Status:
            </Typography>
            <Grid container direction="row">
              <Grid item xs={2}>
                <UserStatusIcon
                  record={record}
                  blocked={carrier?.blockAccess}
                  nameMismatch={nameMismatch}
                  classes={classes.statusIcon}
                />
              </Grid>
              <Grid item xs={10} className={classes.status}>
                <SelectField
                  source="status"
                  record={record}
                  choices={UserStatuses}
                />
              </Grid>
            </Grid>
          </Grid>
          {record.driverCode && (
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Driver Code:
              </Typography>
              <Typography variant="body2">{record.driverCode}</Typography>
            </Grid>
          )}
          {record.userType === "driver" && (
            <DriverIdentityPanel
              identity={identity}
              nameMismatch={nameMismatch}
            />
          )}
          {carrier && (
            <>
              <Grid item xs={12}>
                <Typography variant="overline" color="textSecondary">
                  Carrier:
                </Typography>
                <br />
                <Link
                  to={{
                    pathname: `/carriers/${carrier.carrierId}`,
                    state: { backPath: linkBackPath },
                  }}
                >
                  <Typography variant="body2" color="primary">
                    {carrier.businessName}
                  </Typography>
                </Link>
              </Grid>
              {carrier.photo && (
                <Grid item xs={12}>
                  <Typography variant="overline" color="textSecondary">
                    User Photo:
                  </Typography>
                  <ImageField
                    source="photo"
                    record={carrier}
                    thumbWidth={200}
                    scaleThumb={true}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
